// ===========================================================
// ERP DROPSHIPPER MODULE (Multi-Brand Discounts + Dynamic Shipping)
// ===========================================================

// Global variable to store dropshippers
let DROPSHIPPERS = [];
let isLoadingDropshippers = false;

let hasAttemptedLoad = false;  // ✅ NEW: Track if we already tried loading
// Default discount structure
const DEFAULT_BRAND_DISCOUNTS = {
  'Ermes Sub': { type: 'percentage', value: 5 },
  'Default': { type: 'percentage', value: 5 }
};

// Load Dropshippers from Shopify Customers
async function loadDropshippers() {
  if (isLoadingDropshippers || hasAttemptedLoad) return;
  isLoadingDropshippers = true;
  
  if (!MASTER_DB.customers || MASTER_DB.customers.length === 0) {
    console.log("Customers not loaded yet");
    isLoadingDropshippers = false;
    hasAttemptedLoad = true;
    return;  // No auto-retry!
  }

  // ✅ Use global tag from config
  const DROPSHIPPER_TAG = GLOBAL_CONFIG.DROPSHIPPER_TAG;

  // Filter customers with dropshipper tag
  DROPSHIPPERS = MASTER_DB.customers
    .filter(customer => {
      if (!customer.tags) return false;
      const tags = Array.isArray(customer.tags) ? customer.tags : customer.tags.toString().split(',');
      return tags.some(tag => tag.trim().toLowerCase() === DROPSHIPPER_TAG);
    })
    .map((customer, index) => {
      const config = parseDropshipperConfig(customer.note);
      
      return {
        id: `ds_${customer.id}`,
        name: `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || customer.email.split('@')[0],
        email: customer.email,
        phone: customer.phone || (customer.default_address?.phone) || 'N/A',
        customerId: customer.id,
        brandDiscounts: config.brandDiscounts || {},
        defaultDiscount: config.defaultDiscount || { type: 'percentage', value: 20 },
        shippingRules: config.shippingRules || { freeThreshold: 0, defaultCost: 0, useActual: true },
        notes: customer.note || '',
        totalSpent: parseFloat(customer.total_spent || 0),
        ordersCount: parseInt(customer.orders_count || 0),
        lastOrderDate: customer.last_order_date,
        currency: GLOBAL_CONFIG.BASE_CURRENCY
      };
    });

  console.log(`✅ Loaded ${DROPSHIPPERS.length} dropshippers:`, DROPSHIPPERS);
  isLoadingDropshippers = false;
  hasAttemptedLoad = true;  // ✅ Mark as attempted
  
  // Refresh UI if dropshipper pane is active
   const dsPane = document.getElementById('dropshipper-pane');
  if (dsPane && dsPane.classList.contains('active') && DROPSHIPPERS.length > 0) {
    renderDropshipperUI();
  }
}

// Parse dropshipper configuration
function parseDropshipperConfig(note) {
  const config = {
    brandDiscounts: {},
    defaultDiscount: { type: 'percentage', value: 10 },
    shippingRules: { freeThreshold: 0, defaultCost: 0, useActual: true }
  };

  if (!note) return config;

  try {
    const parts = note.split('|');
    
    parts.forEach(part => {
      const [key, value] = part.split(':');
      if (!key || !value) return;
      
      const cleanKey = key.trim().toUpperCase();
      
      if (cleanKey === 'BRAND') {
        const [brandName, discountStr] = value.split('=');
        if (brandName && discountStr) {
          const cleanBrand = brandName.trim();
          const isFixed = discountStr.toLowerCase().includes('fixed');
          const numValue = parseFloat(discountStr.replace(/[^0-9.]/g, '')) || 20;
          
          config.brandDiscounts[cleanBrand] = {
            type: isFixed ? 'fixed' : 'percentage',
            value: numValue
          };
        }
      }
      else if (cleanKey === 'DEFAULT') {
        const isFixed = value.toLowerCase().includes('fixed');
        const numValue = parseFloat(value.replace(/[^0-9.]/g, '')) || 20;
        config.defaultDiscount = {
          type: isFixed ? 'fixed' : 'percentage',
          value: numValue
        };
      }
      else if (cleanKey === 'SHIPPING') {
        if (value.toLowerCase() === 'actual') {
          config.shippingRules = { freeThreshold: 0, defaultCost: 0, useActual: true };
        } else if (value.toLowerCase().startsWith('free=')) {
          const threshold = parseFloat(value.split('=')[1]) || 500;
          config.shippingRules = { freeThreshold: threshold, defaultCost: 0, useActual: false };
        } else {
          const cost = parseFloat(value) || 0;
          config.shippingRules = { freeThreshold: 0, defaultCost: cost, useActual: false };
        }
      }
    });
  } catch (e) {
    console.error("Error parsing config:", e);
  }

  return config;
}

// Get discount for specific brand
function getBrandDiscount(dropshipper, brandName) {
  if (!brandName) return dropshipper.defaultDiscount;
  
  // Try exact match
  if (dropshipper.brandDiscounts[brandName]) {
    return dropshipper.brandDiscounts[brandName];
  }
  
  // Try case-insensitive
  const brandKey = Object.keys(dropshipper.brandDiscounts).find(
    k => k.toLowerCase() === brandName.toLowerCase()
  );
  
  if (brandKey) {
    return dropshipper.brandDiscounts[brandKey];
  }
  
  // Try default brands
  if (DEFAULT_BRAND_DISCOUNTS[brandName]) {
    return DEFAULT_BRAND_DISCOUNTS[brandName];
  }
  
  return dropshipper.defaultDiscount;
}

// Get shipping from order - FIXED VERSION
function getShippingFromOrder(order, dropshipper) {
  const rules = dropshipper.shippingRules;
  
  if (rules.useActual) {
    let totalShipping = 0;
    
    // Method 1: shipping_lines array
    if (order.shipping_lines && Array.isArray(order.shipping_lines)) {
      order.shipping_lines.forEach(line => {
        totalShipping += parseFloat(line.price || line.cost || 0);
      });
    }
    
    // Method 2: shipping_line object (single)
    if (order.shipping_line && order.shipping_line.price) {
      totalShipping += parseFloat(order.shipping_line.price);
    }
    
    // Method 3: total_shipping field
    if (order.total_shipping) {
      totalShipping += parseFloat(order.total_shipping);
    }
    
    // Method 4: Calculate from total - subtotal - tax + discount
    if (totalShipping === 0 && order.subtotal_price && order.total_price) {
      const subtotal = parseFloat(order.subtotal_price);
      const total = parseFloat(order.total_price);
      const tax = parseFloat(order.total_tax || 0);
      const discount = parseFloat(order.total_discounts || 0);
      
      // total = subtotal + shipping + tax - discount
      // shipping = total - subtotal - tax + discount
      totalShipping = Math.max(0, total - subtotal - tax + discount);
    }
    
    return totalShipping;
  }
  
  // Fixed shipping rules
  const subtotal = parseFloat(order.subtotal_price || order.total_price);
  if (rules.freeThreshold > 0 && subtotal >= rules.freeThreshold) {
    return 0;
  }
  
  return rules.defaultCost;
}
// Helper: Safe class name for CSS
function safeClassName(str) {
  return str.replace(/[^a-zA-Z0-9_-]/g, '_');
}

// Render Dropshipper UI
function renderDropshipperUI() {
  const target = document.getElementById('dropshipper-target');
  if (!target) return;

  // Show loading if not loaded yet
  if (DROPSHIPPERS.length === 0 && !hasAttemptedLoad) {
    target.innerHTML = `
      <div style="text-align: center; padding: 60px 20px;">
        <div style="font-size: 48px; margin-bottom: 20px;">⏳</div>
        <h3 style="color: #1f293b;">Loading Dropshippers...</h3>
        <p style="color: #6b7280;">Found ${MASTER_DB.customers?.length || 0} customers, scanning for dropshippers...</p>
        <button onclick="forceLoadDropshippers()" style="margin-top: 20px; padding: 10px 20px; background: #3b82f6; color: white; border: none; border-radius: 8px; cursor: pointer;">
          🔄 Force Reload
        </button>
      </div>
    `;
    
    // Trigger load if not already loading
    if (!isLoadingDropshippers && !hasAttemptedLoad) {
      loadDropshippers();
    }
    return;
  }

  // Render full UI
  const dropshipperOptions = DROPSHIPPERS.map(ds => 
    `<option value="${ds.id}">${ds.name} (${ds.email})</option>`
  ).join('');

  target.innerHTML = `
    <div class="dropshipper-container">
      <!-- Stats Header -->
      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 15px; margin-bottom: 20px;">
        <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 15px; border-radius: 12px; text-align: center;">
          <div style="font-size: 24px; font-weight: bold;">${DROPSHIPPERS.length}</div>
          <div style="font-size: 12px; opacity: 0.9;">Dropshippers</div>
        </div>
      </div>

      <!-- Filter Panel -->
      <div style="background: white; padding: 20px; border-radius: 12px; margin-bottom: 20px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
        <h3 style="margin: 0 0 15px 0; color: #1f293b;">🔍 Calculate Commission</h3>
        
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px;">
          <div>
            <label style="display: block; margin-bottom: 5px; font-weight: 600; color: #374151;">Select Dropshipper *</label>
            <select id="ds-select" onchange="showDropshipperDetails(); previewFilteredData()" style="width: 100%; padding: 10px; border: 1px solid #d1d5db; border-radius: 8px;">
              <option value="">-- Select --</option>
              ${dropshipperOptions}
            </select>
          </div>

          <div>
            <label style="display: block; margin-bottom: 5px; font-weight: 600; color: #374151;">Start Date *</label>
            <input type="date" id="ds-start-date" onchange="previewFilteredData()" style="width: 100%; padding: 10px; border: 1px solid #d1d5db; border-radius: 8px;">
          </div>

          <div>
            <label style="display: block; margin-bottom: 5px; font-weight: 600; color: #374151;">End Date *</label>
            <input type="date" id="ds-end-date" onchange="previewFilteredData()" style="width: 100%; padding: 10px; border: 1px solid #d1d5db; border-radius: 8px;">
          </div>

          <div>
            <label style="display: block; margin-bottom: 5px; font-weight: 600; color: #374151;">Status</label>
            <select id="ds-status" onchange="previewFilteredData()" style="width: 100%; padding: 10px; border: 1px solid #d1d5db; border-radius: 8px;">
              <option value="all">All Orders</option>
              <option value="paid" selected>Paid</option>
              <option value="fulfilled">Fulfilled</option>
              <option value="paid_fulfilled">Paid & Fulfilled</option>
            </select>
          </div>
        </div>

        <!-- Details Card -->
        <div id="ds-details-card" style="display: none; margin-top: 15px; padding: 15px; background: #f8fafc; border-radius: 8px; border-left: 4px solid #3b82f6;">
          <h4 style="margin: 0 0 10px 0; color: #1e40af;" id="ds-detail-name"></h4>
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px; font-size: 13px; color: #4b5563;">
            <div>📧 <span id="ds-detail-email"></span></div>
            <div>📱 <span id="ds-detail-phone"></span></div>
            <div>📦 Orders: <strong id="ds-detail-orders"></strong></div>
            <div>💰 Spent: <strong id="ds-detail-spent"></strong></div>
          </div>
          <div style="margin-top: 10px;">
            <div style="font-size: 12px; font-weight: 600; color: #374151; margin-bottom: 5px;">Brand Discounts:</div>
            <div id="ds-detail-brands" style="display: flex; flex-wrap: wrap; gap: 5px;"></div>
          </div>
          <div style="margin-top: 8px; font-size: 12px; color: #059669;" id="ds-detail-shipping"></div>
        </div>

        <!-- Buttons -->
        <div style="margin-top: 20px; display: flex; gap: 10px; flex-wrap: wrap;">
          <button onclick="calculateDropshipperReport()" style="background: #3b82f6; color: white; border: none; padding: 12px 24px; border-radius: 8px; font-weight: 600; cursor: pointer;">
            📊 Calculate
          </button>
          <button onclick="exportDropshipperReport('pdf')" id="ds-btn-pdf" style="display: none; background: #ef4444; color: white; border: none; padding: 12px 24px; border-radius: 8px; cursor: pointer;">
            📄 PDF
          </button>
          <button onclick="exportDropshipperReport('excel')" id="ds-btn-excel" style="display: none; background: #10b981; color: white; border: none; padding: 12px 24px; border-radius: 8px; cursor: pointer;">
            📈 Excel
          </button>
          <button onclick="emailDropshipperReport()" id="ds-btn-email" style="display: none; background: #8b5cf6; color: white; border: none; padding: 12px 24px; border-radius: 8px; cursor: pointer;">
            📧 Email
          </button>
        </div>
      </div>

      <!-- Results -->
      <div id="ds-results" style="display: none;">
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 15px; margin-bottom: 20px;">
          <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 20px; border-radius: 12px;">
            <div style="font-size: 14px;">Total Orders</div>
            <div id="ds-total-orders" style="font-size: 28px; font-weight: bold;">0</div>
          </div>
          <div style="background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%); color: white; padding: 20px; border-radius: 12px;">
            <div style="font-size: 14px;">Order Value</div>
            <div id="ds-total-value" style="font-size: 28px; font-weight: bold;">AED 0</div>
          </div>
          <div style="background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%); color: white; padding: 20px; border-radius: 12px;">
            <div style="font-size: 14px;">Retail Cost</div>
            <div id="ds-retail-cost" style="font-size: 28px; font-weight: bold;">AED 0</div>
          </div>
          <div style="background: linear-gradient(135deg, #fa709a 0%, #fee140 100%); color: white; padding: 20px; border-radius: 12px;">
            <div style="font-size: 14px;">Discounts</div>
            <div id="ds-total-discount" style="font-size: 28px; font-weight: bold;">AED 0</div>
          </div>
          <div style="background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); color: white; padding: 20px; border-radius: 12px;">
  <div style="font-size: 14px;">Order Shipping Value</div>
  <div id="ds-your-earnings" style="font-size: 28px; font-weight: bold;">AED 0</div>
  <div style="font-size: 11px; opacity: 0.9; margin-top: 5px;">
    Shipping
  </div>
</div>
          <div style="background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%); color: white; padding: 20px; border-radius: 12px;">
            <div style="font-size: 14px;">DS Profit</div>
            <div id="ds-dropshipper-profit" style="font-size: 28px; font-weight: bold;">AED 0</div>
          </div>
          <div style="background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%); color: #7c2d12; padding: 20px; border-radius: 12px;">
            <div style="font-size: 14px;">Your Profit</div>
            <div id="ds-your-profit" style="font-size: 28px; font-weight: bold;">AED 0</div>
          </div>
        </div>

        <div style="background: white; border-radius: 12px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
          <div style="padding: 15px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; font-weight: 600;">
            Order Breakdown
          </div>
          <div style="overflow-x: auto;">
            <table style="width: 100%; font-size: 12px;">
              <thead>
                <tr style="background: #f1f5f9;">
                  <th style="padding: 10px; text-align: left;">Order #</th>
                  <th style="padding: 10px; text-align: left;">Date</th>
                  <th style="padding: 10px; text-align: left;">Product / Info</th>
                  <th style="padding: 10px; text-align: left;">Brand / Status</th>
                  <th style="padding: 10px; text-align: right;">Qty</th>
                  <th style="padding: 10px; text-align: right;">Orrder Value</th>
                  <th style="padding: 10px; text-align: right;">Retail Value</th>
                  <th style="padding: 10px; text-align: right;">Discount</th>
                  <th style="padding: 10px; text-align: right;">Shipping</th>
                  <th style="padding: 10px; text-align: right; background: #e0f2fe;">Your Profit</th> 
                  <th style="padding: 10px; text-align: right;">DS Profit</th>
                </tr>
              </thead>
              <tbody id="ds-orders-table"></tbody>
              <tfoot style="background: #f8fafc; font-weight: bold;">
                <tr>
                  <td colspan="5" style="padding: 10px; text-align: right;">TOTALS:</td>
                  <td id="ds-foot-total" style="padding: 10px; text-align: right;">AED 0</td>
                  <td id="ds-foot-retail" style="padding: 10px; text-align: right;">AED 0</td>
                  <td id="ds-foot-discount" style="padding: 10px; text-align: right;">AED 0</td>
                  <td id="ds-foot-shipping" style="padding: 10px; text-align: right;">AED 0</td>
                   <td id="ds-foot-your-profit" style="padding: 10px; text-align: right; color: #0369a1; background: #e0f2fe;">AED 0</td> 
                  <td id="ds-foot-ds-profit" style="padding: 10px; text-align: right; color: #059669;">AED 0</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div id="ds-empty" style="text-align: center; padding: 60px 20px; background: white; border-radius: 12px;">
        <div style="font-size: 64px; margin-bottom: 20px;">🚚</div>
        <h3 style="color: #1f293b;">Dropshipper Commission Calculator</h3>
        <p style="color: #6b7280;">Select dropshipper and date range to calculate</p>
        
        <div style="background: #f0fdf4; border: 1px solid #86efac; border-radius: 8px; padding: 15px; text-align: left; max-width: 600px; margin: 20px auto; font-size: 13px;">
          <h4 style="margin: 0 0 10px 0; color: #166534;">💡 Configuration Guide:</h4>
          <ol style="margin: 0; padding-left: 20px; color: #166534; line-height: 1.8;">
            <li>Go to Shopify Admin → Customers</li>
            <li>Add tag: <strong>fishondropshipper</strong></li>
            <li>In notes: <code>BRAND:Ocean Devil=25%|BRAND:Abu Garcia=15%|SHIPPING:actual</code></li>
          </ol>
        </div>
      </div>
    </div>
  `;

  // Set default dates
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 30);
  
  document.getElementById('ds-start-date').value = startDate.toISOString().split('T')[0];
  document.getElementById('ds-end-date').value = endDate.toISOString().split('T')[0];
}

// Force reload dropshippers
window.forceLoadDropshippers = function() {
 isLoadingDropshippers = false;
  hasAttemptedLoad = false;  
  DROPSHIPPERS = [];
  loadDropshippers();
};

// Show dropshipper details
window.showDropshipperDetails = function() {
  const dsId = document.getElementById('ds-select').value;
  const card = document.getElementById('ds-details-card');
  
  if (!dsId) {
    card.style.display = 'none';
    return;
  }

  const ds = DROPSHIPPERS.find(d => d.id === dsId);
  if (!ds) return;

  document.getElementById('ds-detail-name').innerText = ds.name;
  document.getElementById('ds-detail-email').innerText = ds.email;
  document.getElementById('ds-detail-phone').innerText = ds.phone;
  document.getElementById('ds-detail-orders').innerText = ds.ordersCount;
  document.getElementById('ds-detail-spent').innerText = formatPrice(ds.totalSpent);
  
  // Brand discounts
  const brandsDiv = document.getElementById('ds-detail-brands');
  const entries = Object.entries(ds.brandDiscounts);
  
  if (entries.length > 0) {
    brandsDiv.innerHTML = entries.map(([brand, disc]) => `
      <span style="background: #dbeafe; color: #1e40af; padding: 2px 8px; border-radius: 12px; font-size: 11px;">
        ${brand}: ${disc.value}${disc.type === 'percentage' ? '%' : ' AED'}
      </span>
    `).join('');
  } else {
    brandsDiv.innerHTML = `<span style="color: #6b7280;">Default: ${ds.defaultDiscount.value}%</span>`;
  }
  
  // Shipping
  const shipText = ds.shippingRules.useActual ? 
    'Using actual shipping from orders' : 
    (ds.shippingRules.freeThreshold > 0 ? `Free above AED ${ds.shippingRules.freeThreshold}` : `Fixed: AED ${ds.shippingRules.defaultCost}`);
  
  document.getElementById('ds-detail-shipping').innerText = '🚚 ' + shipText;
  card.style.display = 'block';
};

// Preview filtered data
window.previewFilteredData = function() {
  const dsId = document.getElementById('ds-select').value;
  const startDate = new Date(document.getElementById('ds-start-date').value);
  const endDate = new Date(document.getElementById('ds-end-date').value);
  endDate.setHours(23, 59, 59, 999);
  const status = document.getElementById('ds-status').value;

  if (!dsId) return;

  const dropshipper = DROPSHIPPERS.find(d => d.id === dsId);
  if (!dropshipper) return;
  
  // Filter orders with DROPSHIPPER EMAIL CHECK
  const filteredOrders = MASTER_DB.orders.filter(order => {
    const orderDate = new Date(order.created_at);
    const dateMatch = orderDate >= startDate && orderDate <= endDate;
    
const orderEmail = order.email || order.customer?.email;
const customerMatch = orderEmail && orderEmail.toLowerCase() === dropshipper.email.toLowerCase();
    
    let statusMatch = true;
    if (status === 'paid') statusMatch = order.financial_status === 'paid';
    else if (status === 'fulfilled') statusMatch = order.fulfillment_status === 'fulfilled';
    else if (status === 'paid_fulfilled') {
      statusMatch = order.financial_status === 'paid' && order.fulfillment_status === 'fulfilled';
    }
    
    return dateMatch && customerMatch && statusMatch;
  });

  // Show preview in details card
  const card = document.getElementById('ds-details-card');
  let previewDiv = document.getElementById('ds-preview-data');
  
  const totalItems = filteredOrders.reduce((sum, o) => sum + (o.line_items?.length || 0), 0);
  const totalValue = filteredOrders.reduce((sum, o) => {
  const orderCurrency = getOrderCurrency(o);
  const orderTotal = parseFloat(o.total_price || 0);
  return sum + convertToStoreCurrency(orderTotal, orderCurrency);
}, 0);
  
  const previewHTML = `
    <div id="ds-preview-data" style="margin-top: 15px; padding: 15px; background: #ecfdf5; border-radius: 8px; border: 1px solid #6ee7b7;">
      <div style="font-size: 13px; font-weight: 600; color: #065f46; margin-bottom: 10px;">
        📊 Filter Preview for <strong>${dropshipper.name}</strong>
        <span style="font-size: 11px; color: #6b7280; font-weight: normal;">(Click Calculate for full report)</span>
      </div>
      <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; font-size: 12px;">
        <div style="text-align: center; background: white; padding: 10px; border-radius: 6px;">
          <div style="font-size: 22px; font-weight: bold; color: #059669;">${filteredOrders.length}</div>
          <div style="color: #6b7280; margin-top: 4px;">Orders Found</div>
        </div>
        <div style="text-align: center; background: white; padding: 10px; border-radius: 6px;">
          <div style="font-size: 22px; font-weight: bold; color: #059669;">${totalItems}</div>
          <div style="color: #6b7280; margin-top: 4px;">Items</div>
        </div>
        <div style="text-align: center; background: white; padding: 10px; border-radius: 6px;">
          <div style="font-size: 22px; font-weight: bold; color: #059669;">${formatPrice(totalValue)}</div>
          <div style="color: #6b7280; margin-top: 4px;">Total Value</div>
        </div>
      </div>
    </div>
  `;
  
  if (!previewDiv) {
    card.insertAdjacentHTML('beforeend', previewHTML);
  } else {
    previewDiv.outerHTML = previewHTML;
  }
};

// Calculate report - FIXED with proper currency conversion
window.calculateDropshipperReport = function() {
  const dsId = document.getElementById('ds-select').value;
  const startDateStr = document.getElementById('ds-start-date').value;
  const endDateStr = document.getElementById('ds-end-date').value;
  const status = document.getElementById('ds-status').value;

  if (!dsId) {
    alert("Please select a dropshipper!");
    return;
  }

  const dropshipper = DROPSHIPPERS.find(d => d.id === dsId);
  if (!dropshipper) {
    alert("Dropshipper not found!");
    return;
  }

  // ✅ FIX: Proper date parsing with time reset
  const startDate = new Date(startDateStr);
  startDate.setHours(0, 0, 0, 0);
  const endDate = new Date(endDateStr);
  endDate.setHours(23, 59, 59, 999);
  
  console.log("=== DEBUG ===");
  console.log("Dropshipper:", dropshipper.name, "Email:", dropshipper.email);
  console.log("Date range:", startDate, "to", endDate);
  console.log("Status:", status);
  console.log("Total orders:", MASTER_DB.orders.length);

  // ✅ FIX: Better order filtering with debug logs
  const filteredOrders = MASTER_DB.orders.filter(order => {
    // Date check
    const orderDate = new Date(order.created_at);
    const dateMatch = orderDate >= startDate && orderDate <= endDate;
    
    // ✅ FIX: Try multiple email sources
    const orderEmail = (
  order.email || 
  order.customer?.email || 
  order.customer_email || 
  order.billing_address?.email ||
  ""
).toLowerCase().trim();
    
    const dsEmail = dropshipper.email.toLowerCase().trim();
    const customerMatch = orderEmail === dsEmail;
    
    // Status check
    let statusMatch = true;
    if (status === 'paid') statusMatch = order.financial_status === 'paid';
    else if (status === 'fulfilled') statusMatch = order.fulfillment_status === 'fulfilled';
    else if (status === 'paid_fulfilled') {
      statusMatch = order.financial_status === 'paid' && order.fulfillment_status === 'fulfilled';
    }
    
    // Debug log for each order
    if (customerMatch || orderEmail.includes('mayed')) {
      console.log(`Order ${order.name}: Email=${orderEmail}, Date=${orderDate}, DateMatch=${dateMatch}, Status=${order.financial_status}`);
    }
    
    return dateMatch && customerMatch && statusMatch;
  });

  console.log("Found orders:", filteredOrders.length);
  console.log("=============");

  if (filteredOrders.length === 0) {
    alert(`No orders found for ${dropshipper.name}!\n\nDebug: Check console for email matching details.`);
    return;
  }
  // Calculate line items
  const calculatedItems = [];
  
  filteredOrders.forEach(order => {
    // 🔥 Get order currency properly
    const orderCurrency = getOrderCurrency(order);
    
    // 🔥 Get order total in original currency
    const orderTotalOriginal = parseFloat(order.total_price || 0);
    
    // 🔥 CONVERT to AED
    const orderTotalAED = convertToStoreCurrency(orderTotalOriginal, orderCurrency);
    
    console.log(`Order ${order.name}: ${orderTotalOriginal} ${orderCurrency} = ${orderTotalAED} AED`);
    
    const lineItems = order.line_items || [];
    const orderShipping = getShippingFromOrder(order, dropshipper);
    const orderSubtotal = parseFloat(order.subtotal_price || order.total_price);
    
    // ✅ DEBUG: Check order structure
    console.log("=== ORDER DEBUG #" + order.name + " ===");
    console.log("shipping_lines:", order.shipping_lines);
    console.log("shipping_line:", order.shipping_line);
    console.log("total_shipping:", order.total_shipping);
    console.log("subtotal_price:", order.subtotal_price);
    console.log("total_price:", order.total_price);
    console.log("total_tax:", order.total_tax);
    console.log("total_discounts:", order.total_discounts);
    console.log("Order Shipping extracted:", orderShipping);
    
    // Convert to AED
    const orderSubtotalAED = convertToStoreCurrency(orderSubtotal, orderCurrency);
    const orderShippingAED = convertToStoreCurrency(orderShipping, orderCurrency);
    
    console.log("Order Subtotal AED:", orderSubtotalAED, "Shipping AED:", orderShippingAED);
    
    lineItems.forEach(item => {
      const product = MASTER_DB.products.find(p => 
        p.variants.some(v => v.id == item.variant_id)
      );
      const variant = product?.variants.find(v => v.id == item.variant_id);
      
      const brandName = item.vendor || product?.vendor || 'Unknown';
      
      // Item price in original currency
      const itemPriceOriginal = parseFloat(item.price);
      const itemPriceAED = convertToStoreCurrency(itemPriceOriginal, orderCurrency);
      const orderPriceOriginal = parseFloat(item.price);
      
      // Retail price
      const retailPriceOriginal = parseFloat(variant?.price || item.price);
      const retailPriceAED = parseFloat(variant?.price || item.price) || 0;
      
      // ✅ ADD THIS LINE - Define the missing variable
      const retailPriceCurrency = GLOBAL_CONFIG.BASE_CURRENCY || 'AED';
      
      const quantity = item.quantity;
      
      const discount = getBrandDiscount(dropshipper, brandName);
      
      // Calculate discount on AED values
      let discountAmountAED = 0;
      if (discount.type === 'percentage') {
        discountAmountAED = (retailPriceAED * quantity) * (discount.value / 100);
      } else {
        discountAmountAED = convertToStoreCurrency(discount.value, dropshipper.currency || 'AED') * quantity;
      }
      
      const itemSubtotalAED = itemPriceAED * quantity;
      const retailCostAED = retailPriceAED * quantity;
      
      // ✅ FIX: Ensure shippingShareAED is calculated correctly
      let shippingShareAED = 0;
      if (itemSubtotalAED > 0 && orderSubtotalAED > 0) {
        shippingShareAED = (orderShippingAED * (itemSubtotalAED / orderSubtotalAED));
      }
      
      console.log(`Item: ${item.title} | Qty: ${quantity} | Subtotal: ${itemSubtotalAED} | Shipping Share: ${shippingShareAED}`);
      
      // Your Profit = (Retail Cost - Discount) + Shipping
      const yourProfitAED = (retailCostAED - discountAmountAED) + shippingShareAED;
      
      // DS Profit = Order Value - Your Profit
      const dropshipperProfitAED = itemSubtotalAED - yourProfitAED;
      
      calculatedItems.push({
        order: order,
        item: item,
        brand: brandName,
        quantity: quantity,
        orderPriceOriginal: orderPriceOriginal,
        orderCurrency: orderCurrency,
        retailPriceOriginal: retailPriceOriginal,
        retailCurrency: retailPriceCurrency,
        orderPriceAED: itemPriceAED,
        retailPriceAED: retailPriceAED,
        itemSubtotalAED: itemSubtotalAED,
        retailCostAED: retailCostAED,
        discount: discount,
        discountAmountAED: discountAmountAED,
        shippingShareAED: shippingShareAED,
        yourProfitAED: yourProfitAED,
        dropshipperProfitAED: dropshipperProfitAED,
        // For display
        itemSubtotal: itemSubtotalAED,
        retailCost: retailCostAED,
        discountAmount: discountAmountAED,
        shippingShare: shippingShareAED,
        yourProfit: yourProfitAED,
        dropshipperProfit: dropshipperProfitAED
      });
    });
  });

  if (calculatedItems.length === 0) {
    alert("No items found in orders!");
    return;
  }

  window.currentDropshipperReport = {
    dropshipper: dropshipper,
    startDate: startDate,
    endDate: endDate,
    items: calculatedItems,
    totals: {
      orders: filteredOrders.length,
      items: calculatedItems.length,
      orderValue: calculatedItems.reduce((a, b) => a + b.itemSubtotalAED, 0),
      retailCost: calculatedItems.reduce((a, b) => a + b.retailCostAED, 0),
      discount: calculatedItems.reduce((a, b) => a + b.discountAmountAED, 0),
      shipping: calculatedItems.reduce((a, b) => a + b.shippingShareAED, 0),
      yourProfit: calculatedItems.reduce((a, b) => a + b.yourProfitAED, 0),
      dropshipperProfit: calculatedItems.reduce((a, b) => a + b.dropshipperProfitAED, 0)
    }
  };
  
  console.log("Report Totals:", window.currentDropshipperReport.totals);
  displayResults(window.currentDropshipperReport);
};
// Display results with ORDER TREE structure
function displayResults(report) {
  document.getElementById('ds-empty').style.display = 'none';
  document.getElementById('ds-results').style.display = 'block';
  
  document.getElementById('ds-btn-pdf').style.display = 'inline-block';
  document.getElementById('ds-btn-excel').style.display = 'inline-block';
  document.getElementById('ds-btn-email').style.display = 'inline-block';
  const yourProfit = report.totals.retailCost - report.totals.discount + report.totals.shipping;
document.getElementById('ds-total-orders').innerText = report.totals.orders;
document.getElementById('ds-total-value').innerText = formatPrice(report.totals.orderValue, 'AED');
document.getElementById('ds-retail-cost').innerText = formatPrice(report.totals.retailCost, 'AED');
document.getElementById('ds-total-discount').innerText = formatPrice(report.totals.discount, 'AED');
document.getElementById('ds-your-earnings').innerText = formatPrice(report.totals.shipping, 'AED');
document.getElementById('ds-dropshipper-profit').innerText = formatPrice(report.totals.dropshipperProfit, 'AED');
document.getElementById('ds-your-profit').innerText = formatPrice(report.totals.yourProfit, 'AED');

  // Group items by ORDER NUMBER
  const groupedByOrder = {};
  report.items.forEach(item => {
    const orderId = item.order.name;
    if (!groupedByOrder[orderId]) {
      groupedByOrder[orderId] = {
        order: item.order,
        currency: item.orderCurrency,
        items: [],
        totals: {
          qty: 0,
          subtotal: 0,
          retail: 0,
          shipping: 0, 
          discount: 0,
          dsProfit: 0
        }
      };
    }
    groupedByOrder[orderId].items.push(item);
    groupedByOrder[orderId].totals.qty += item.quantity;
    groupedByOrder[orderId].totals.subtotal += item.itemSubtotalAED;
    groupedByOrder[orderId].totals.retail += item.retailCostAED;
    groupedByOrder[orderId].totals.discount += item.discountAmountAED;
    groupedByOrder[orderId].totals.shipping += item.shippingShareAED;  // ✅ ADD THIS
    groupedByOrder[orderId].totals.dsProfit += item.dropshipperProfitAED;
  });

  // Generate HTML for tree structure
  const tbody = document.getElementById('ds-orders-table');
  let html = '';
  
  Object.entries(groupedByOrder).forEach(([orderId, group], orderIndex) => {
    const order = group.order;
    const date = new Date(order.created_at).toLocaleDateString('en-GB');
    const isEven = orderIndex % 2 === 0;
    const rowBg = isEven ? '#fafafa' : '#ffffff';
    const safeOrderClass = safeClassName(orderId);
    
    // Show currency badge if not AED
    const currencyBadge = group.currency !== 'AED' ? 
      `<span style="background: #fef3c7; color: #92400e; padding: 2px 6px; border-radius: 4px; font-size: 10px; margin-left: 8px;">${group.currency}</span>` : '';
    
    // ORDER HEADER ROW
    html += `
      <tr class="order-header" style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; cursor: pointer;" 
          onclick="toggleOrderItems('${safeOrderClass}')" 
          data-order="${safeOrderClass}">
        <td style="padding: 12px; font-weight: bold; font-size: 13px;">
          <span class="toggle-icon-${safeOrderClass}" style="display: inline-block; margin-right: 8px; transition: transform 0.2s;">▼</span>
          <a href="https://${window.Shopify.shop}/admin/orders/${order.id}" target="_blank" 
             style="color: white; text-decoration: underline; font-size: 12px;">
            ${orderId}
          </a>
          ${currencyBadge}
        </td>
        <td style="padding: 12px; font-size: 12px;">${date}</td>
        <td style="padding: 12px; font-size: 12px; font-weight: 600;">
          📦 ${group.items.length} Item${group.items.length > 1 ? 's' : ''}
        </td>
        <td style="padding: 12px; font-size: 11px;">
          <span style="background: rgba(255,255,255,0.2); padding: 3px 8px; border-radius: 4px;">
            ${order.financial_status || 'N/A'}
          </span>
        </td>
        <td style="padding: 12px; text-align: right; font-weight: bold; font-size: 12px;">
          ${group.totals.qty}
        </td>
        <td style="padding: 12px; text-align: right; font-weight: bold; font-size: 12px;">
          ${formatPrice(group.totals.subtotal, 'AED')}
        </td>
        <td style="padding: 12px; text-align: right; font-size: 11px; opacity: 0.9;">
          ${formatPrice(group.totals.retail, 'AED')}
        </td>
        <td style="padding: 12px; text-align: right; font-size: 11px; color: #a7f3d0;">
          -${formatPrice(group.totals.discount, 'AED')}
        </td>
        <td style="padding: 12px; text-align: right; font-size: 11px; color: #fef08a;">  <!-- ✅ ADD THIS COLUMN -->
      +${formatPrice(group.totals.shipping, 'AED')}
    </td>
        <td style="padding: 12px; text-align: right; font-weight: bold; font-size: 12px; color: #a7f3d0;">
          ${formatPrice(group.totals.dsProfit, 'AED')}
        </td>
         <td style="padding: 12px; text-align: right; font-weight: bold; font-size: 12px; background: rgba(255,255,255,0.1);">  <!-- ✅ YOUR PROFIT -->
      ${formatPrice(group.totals.retail - group.totals.discount + group.totals.shipping, 'AED')}
    </td>
      </tr>
    `;
    
    // ITEM ROWS
    group.items.forEach((item, itemIndex) => {
      const indent = itemIndex === group.items.length - 1 ? '└─' : '├─';
      const originalPriceInfo = item.orderCurrency !== 'AED' ? 
        `<br><small style="color: #9ca3af;">${formatPrice(item.orderPriceOriginal, item.orderCurrency)}</small>` : '';
        const itemYourProfit = item.retailCostAED - item.discountAmountAED + item.shippingShareAED;

      html += `
        <tr class="order-item-${safeOrderClass}" style="background: ${rowBg}; border-bottom: 1px solid #e2e8f0;">
          <td style="padding: 8px 12px 8px 30px; color: #6b7280; font-size: 11px;">
            ${indent} ${itemIndex + 1}
          </td>
          <td style="padding: 8px 12px; color: #9ca3af; font-size: 10px;">
            ${date}
          </td>
          <td style="padding: 8px 12px; font-size: 11px; color: #374151;">
            ${item.item.title.substring(0, 35)}${item.item.title.length > 35 ? '...' : ''}
          </td>
          <td style="padding: 8px 12px; font-size: 10px;">
            <span style="background: #e0e7ff; color: #3730a3; padding: 2px 6px; border-radius: 4px;">
              ${item.brand}
            </span>
          </td>
          <td style="padding: 8px 12px; text-align: right; font-size: 11px; color: #6b7280;">
            ${item.quantity}
          </td>
          <td style="padding: 8px 12px; text-align: right; font-size: 11px; font-weight: 500;">
            ${formatPrice(item.itemSubtotalAED, 'AED')}
            ${originalPriceInfo}
          </td>
          <td style="padding: 8px 12px; text-align: right; color: #6b7280; font-size: 10px;">
            ${formatPrice(item.retailCostAED, 'AED')}
          </td>
          <td style="padding: 8px 12px; text-align: right; color: #059669; font-size: 10px;">
            -${formatPrice(item.discountAmountAED, 'AED')}
            <br><small style="color: #9ca3af;">(${item.discount.value}${item.discount.type === 'percentage' ? '%' : ' AED'})</small>
          </td>
           <td style="padding: 8px 12px; text-align: right; color: #ca8a04; font-size: 10px;">  <!-- ✅ ADD THIS -->
        +${formatPrice(item.shippingShareAED, 'AED')}
      </td>
          <td style="padding: 8px 12px; text-align: right; font-weight: 500; font-size: 11px; color: ${item.dropshipperProfitAED >= 0 ? '#059669' : '#dc2626'}; background: ${item.dropshipperProfitAED >= 0 ? '#f0fdf4' : '#fef2f2'};">
            ${formatPrice(item.dropshipperProfitAED, 'AED')}
          </td>
        <td style="padding: 8px 12px; text-align: right; font-weight: 500; font-size: 11px; color: #0369a1; background: #e0f2fe;">  <!-- ✅ YOUR PROFIT -->
        ${formatPrice(itemYourProfit, 'AED')}
      </td>
        </tr>
      `;
    });
  });

  tbody.innerHTML = html;

  // Footer
document.getElementById('ds-foot-total').innerText = formatPrice(report.totals.orderValue, 'AED');
document.getElementById('ds-foot-retail').innerText = formatPrice(report.totals.retailCost, 'AED');
document.getElementById('ds-foot-discount').innerText = formatPrice(report.totals.discount, 'AED');
document.getElementById('ds-foot-shipping').innerText = formatPrice(report.totals.shipping, 'AED'); 
document.getElementById('ds-foot-ds-profit').innerText = formatPrice(report.totals.dropshipperProfit, 'AED');
const totalYourProfit = report.totals.retailCost - report.totals.discount + report.totals.shipping;
document.getElementById('ds-foot-your-profit').innerText = formatPrice(totalYourProfit, 'AED');
}
// Toggle order items expand/collapse
window.toggleOrderItems = function(safeOrderClass) {
  const rows = document.querySelectorAll(`.order-item-${safeOrderClass}`);
  const header = document.querySelector(`tr[data-order="${safeOrderClass}"]`);
  const icon = document.querySelector(`.toggle-icon-${safeOrderClass}`);
  
  if (!rows.length || !header) return;
  
  const isHidden = rows[0].style.display === 'none';
  
  rows.forEach(row => {
    row.style.display = isHidden ? '' : 'none';
  });
  
  if (icon) {
    icon.style.transform = isHidden ? 'rotate(0deg)' : 'rotate(-90deg)';
  }
};

// Export functions
window.exportDropshipperReport = function(type) {
  const report = window.currentDropshipperReport;
  if (!report) {
    alert("Calculate report first!");
    return;
  }
  
  if (type === 'pdf') {
    generatePDF(report);
  } else {
    generateExcel(report);
  }
};

function generatePDF(report) {
  const { jsPDF } = window.jspdf;
  const doc = new jsPDF('l', 'mm', 'a4'); // landscape
  const ds = report.dropshipper;
  
  doc.setFontSize(16);
  doc.setTextColor(37, 99, 235);
  doc.text('Dropshipper Commission Report', 14, 15);
  
  doc.setFontSize(11);
  doc.setTextColor(100);
  doc.text(`Dropshipper: ${ds.name}`, 14, 25);
  doc.text(`Email: ${ds.email}`, 14, 32);
  doc.text(`Period: ${report.startDate.toLocaleDateString()} to ${report.endDate.toLocaleDateString()}`, 14, 39);

  // Summary box
  doc.setFillColor(240, 249, 255);
  doc.roundedRect(14, 45, 270, 20, 3, 3, 'F');
  doc.setFontSize(10);
  doc.setTextColor(0);
  doc.text(`Orders: ${report.totals.orders} | Items: ${report.totals.items} | Value: ${formatPrice(report.totals.orderValue)} | Discount: ${formatPrice(report.totals.discount)} | Shipping: ${formatPrice(report.totals.shipping)} | Your Profit: ${formatPrice(report.totals.retailCost - report.totals.discount + report.totals.shipping)} | DS Profit: ${formatPrice(report.totals.dropshipperProfit)}`, 20, 57);

  // ✅ FIX: Reduced columns for PDF (remove some columns to fit)
  const head = [['Order', 'Date', 'Product', 'Brand', 'Qty', 'Total Value', 'Retail Value', 'Disc', 'Shipping', 'Your Profit', 'DS Profit']];
  const body = report.items.map(i => [
    i.order.name,
    new Date(i.order.created_at).toLocaleDateString(),
    i.item.title.substring(0, 15), // Shorter product name
    i.brand.substring(0, 10), // Shorter brand name
    i.quantity,
    formatPrice(i.itemSubtotal),
    formatPrice(i.retailCost),
    formatPrice(i.discountAmount),
    formatPrice(i.shippingShare),
    formatPrice(i.retailCostAED - i.discountAmountAED + i.shippingShareAED), // Your Profit
    formatPrice(i.dropshipperProfit)
  ]);

  doc.autoTable({
    startY: 72,
    head: head,
    body: body,
    theme: 'striped',
    headStyles: { 
      fillColor: [37, 99, 235], 
      textColor: 255, 
      fontSize: 8, // Smaller font
      cellPadding: 2
    },
    bodyStyles: { 
      fontSize: 7, // Smaller font
      cellPadding: 2
    },
    columnStyles: {
      0: { cellWidth: 20 }, // Order
      1: { cellWidth: 18 }, // Date
      2: { cellWidth: 35 }, // Product
      3: { cellWidth: 20 }, // Brand
      4: { cellWidth: 10 }, // Qty
      5: { cellWidth: 22 }, // Total
      6: { cellWidth: 22 }, // Retail
      7: { cellWidth: 20 }, // Disc
      8: { cellWidth: 20 }, // Ship
      9: { cellWidth: 22 }, // Your Prof
      10: { cellWidth: 22 } // DS Prof
    },
    foot: [[
      '', 
      '', 
      '', 
      '', 
      'TOTAL', 
      formatPrice(report.totals.orderValue), 
      formatPrice(report.totals.retailCost), 
      formatPrice(report.totals.discount), 
      formatPrice(report.totals.shipping),
      formatPrice(report.totals.retailCost - report.totals.discount + report.totals.shipping), // Your Profit
      formatPrice(report.totals.dropshipperProfit)
    ]],
    footStyles: { 
      fillColor: [241, 245, 249], 
      textColor: 0, 
      fontStyle: 'bold',
      fontSize: 8
    },
    margin: { left: 10, right: 10 }, // Smaller margins
    tableWidth: 'wrap' // Auto width
  });

  doc.save(`DS_Report_${ds.name}_${Date.now()}.pdf`);
}

function generateExcel(report) {
  const ds = report.dropshipper;
  let csv = "data:text/csv;charset=utf-8,";
  
  csv += "Dropshipper Commission Report\n";
  csv += `Dropshipper,${ds.name}\n`;
  csv += `Email,${ds.email}\n`;
  csv += `Period,${report.startDate.toLocaleDateString()} to ${report.endDate.toLocaleDateString()}\n\n`;
  
  csv += "SUMMARY\n";
  csv += `Orders,${report.totals.orders}\n`;
  csv += `Items,${report.totals.items}\n`;
  csv += `Order Value,${report.totals.orderValue}\n`;
  csv += `Retail Cost,${report.totals.retailCost}\n`;
  csv += `Discounts,${report.totals.discount}\n`;
  csv += `Dropshipper Profit,${report.totals.dropshipperProfit}\n\n`;
  
  csv += "DETAILS\n";
  csv += "Order,Date,Product,Brand,Qty,Order Total,Retail,Discount,DS Profit\n";
  
  report.items.forEach(i => {
    csv += `${i.order.name},${new Date(i.order.created_at).toLocaleDateString()},"${i.item.title}",${i.brand},${i.quantity},${i.itemSubtotal},${i.retailCost},${i.discountAmount},${i.dropshipperProfit}\n`;
  });

  const link = document.createElement("a");
  link.setAttribute("href", encodeURI(csv));
  link.setAttribute("download", `DS_Report_${ds.name}_${Date.now()}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
}

// Email
window.emailDropshipperReport = async function() {
  const report = window.currentDropshipperReport;
  if (!report) {
    alert("Calculate report first!");
    return;
  }

  const ds = report.dropshipper;
  if (!confirm(`Send report to ${ds.name} at ${ds.email}?`)) return;

  // Create simple PDF
  const { jsPDF } = window.jspdf;
  const doc = new jsPDF();
  
  doc.setFontSize(16);
  doc.text('Dropshipper Commission Report', 14, 20);
  doc.setFontSize(12);
  doc.text(`Dropshipper: ${ds.name}`, 14, 35);
  doc.text(`Period: ${report.startDate.toLocaleDateString()} to ${report.endDate.toLocaleDateString()}`, 14, 45);
  doc.text(`Total Orders: ${report.totals.orders}`, 14, 60);
  doc.text(`Order Value: ${formatPrice(report.totals.orderValue)}`, 14, 70);
  doc.text(`Your Commission: ${formatPrice(report.totals.dropshipperProfit)}`, 14, 80);
  
  const pdfBase64 = doc.output('datauristring').split(',')[1];

  try {
    const res = await fetch(SYSTEM.BRIDGE, {
      method: 'POST',
      mode: 'cors',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify({
        auth_key: SYSTEM.AUTH,
        endpoint: '/send_dropshipper_report',
        data: { dropshipper: ds, report: report, pdfBase64: pdfBase64 }
      })
    });

    const result = await res.json();
    if (result.status === "success") {
      alert(`✅ Report sent to ${ds.email}!`);
    } else {
      throw new Error(result.message);
    }
  } catch (e) {
    console.error("Email error:", e);
    alert("Failed to send email. Please download and send manually.");
  }
};

window.initDropshipperModule = function() {
  console.log("Init dropshipper. Customers:", MASTER_DB.customers?.length, "Dropshippers:", DROPSHIPPERS.length);
  
  renderDropshipperUI();
  
  // ✅ FIX: Add hasAttemptedLoad check
  if (MASTER_DB.customers?.length > 0 && DROPSHIPPERS.length === 0 && !isLoadingDropshippers && !hasAttemptedLoad) {
    loadDropshippers();
  }
};